import { useEffect, useState } from 'react'
import { Network } from 'lucide-react'
import { CountBadge } from '../index.js'
import { clearApiCalls, useApiCalls, type ApiCallRecord } from './apiCallLog.js'

function pathOf(url: string): string {
  try {
    const parsed = new URL(url)
    return `${parsed.pathname}${parsed.search}`
  } catch {
    return url
  }
}

function isFailure(call: ApiCallRecord): boolean {
  return call.status === null || call.status >= 400
}

function CodeBlock({ label, text }: { label: string; text: string }) {
  return (
    <div className="mb-3">
      <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1">{label}</div>
      <pre className="bg-canvas border border-marine/15 p-2 text-[11px] font-mono text-ink whitespace-pre-wrap break-all max-h-72 overflow-auto">
        {text === '' ? '(empty)' : text}
      </pre>
    </div>
  )
}

function CallDetail({ call }: { call: ApiCallRecord }) {
  const headers = Object.entries(call.requestHeaders)
    .map(([name, value]) => `${name}: ${value}`)
    .join('\n')

  return (
    <div className="px-4 pb-3">
      <div className="text-[11px] font-mono text-ink-mid break-all mb-3">{call.url}</div>
      <CodeBlock label="Request headers" text={headers} />
      <CodeBlock label="Request body" text={call.requestBody} />
      {call.networkError === null ? (
        <CodeBlock label={`Response ${call.status ?? ''}`} text={call.responseBody} />
      ) : (
        <CodeBlock label="No response" text={call.networkError} />
      )}
    </div>
  )
}

function CallRow({ call, isOpen, onToggle }: { call: ApiCallRecord; isOpen: boolean; onToggle: () => void }) {
  return (
    <li className="border-b border-marine/10">
      <button type="button" onClick={onToggle} aria-expanded={isOpen} className="w-full text-left px-4 py-2 flex items-center gap-3 hover:bg-canvas">
        <span className="text-[10px] font-bold font-mono text-marine w-12 shrink-0">{call.method}</span>
        <span className="text-xs font-mono text-ink break-all flex-1">{pathOf(call.url)}</span>
        <span className={`text-[11px] font-bold shrink-0 ${isFailure(call) ? 'text-coral' : 'text-green'}`}>{call.status ?? 'failed'}</span>
        <span className="text-[10px] text-ink-mid w-14 text-right shrink-0">{call.durationMs} ms</span>
      </button>
      {isOpen && <CallDetail call={call} />}
    </li>
  )
}

/**
 * Every API call this page has made, newest first, with the request and
 * response behind it. Rendered in the admin shell, for a signed-in
 * SeeWhoCares admin only - it is a developer's view, not an operator's -
 * though every shell's calls are recorded, so an admin sees the lot. Passwords, tokens and signed assertions are redacted before a
 * call is recorded, and the log lives in memory only.
 */
export function ApiCallInspector() {
  const calls = useApiCalls()
  const [isOpen, setIsOpen] = useState(false)
  const [openCallId, setOpenCallId] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) {
      return
    }
    function closeOnEscape(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }
    window.addEventListener('keydown', closeOnEscape)
    return () => window.removeEventListener('keydown', closeOnEscape)
  }, [isOpen])

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        aria-label={`API calls (${calls.length})`}
        title="API calls"
        className="fixed bottom-4 right-4 z-40 flex h-9 w-9 items-center justify-center rounded-full bg-marine-dk text-white shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-coral"
      >
        <Network aria-hidden="true" strokeWidth={1.75} className="h-4 w-4" />
        <CountBadge count={calls.length} label="calls" />
      </button>
    )
  }

  return (
    <aside aria-label="API calls" className="fixed inset-y-0 right-0 z-40 w-full max-w-xl bg-white border-l border-marine/15 shadow-lg flex flex-col">
      <div className="px-4 py-3 border-b border-marine/15 flex items-center justify-between gap-3">
        <div>
          <div className="text-[9px] font-bold uppercase tracking-[2.5px] text-coral">Inspector</div>
          <div className="text-sm font-black text-ink">API calls</div>
        </div>
        <div className="flex items-center gap-4">
          <button type="button" onClick={clearApiCalls} disabled={calls.length === 0} className="text-[11px] font-bold text-marine disabled:text-ink-mid/50">
            Clear
          </button>
          <button type="button" onClick={() => setIsOpen(false)} className="text-[11px] font-bold text-ink-mid hover:text-ink">
            Close
          </button>
        </div>
      </div>
      <p className="px-4 py-2 text-[11px] text-ink-mid border-b border-marine/10">
        The last {calls.length === 1 ? 'call' : `${calls.length} calls`} this page made. Passwords, tokens and signed assertions are redacted. Nothing
        here is stored; a reload clears it.
      </p>
      {calls.length === 0 ? (
        <div className="p-4 text-xs text-ink-mid">No API calls yet.</div>
      ) : (
        <ul className="flex-1 overflow-auto">
          {calls.map((call) => (
            <CallRow key={call.id} call={call} isOpen={openCallId === call.id} onToggle={() => setOpenCallId(openCallId === call.id ? null : call.id)} />
          ))}
        </ul>
      )}
    </aside>
  )
}
